import { Injectable, OnDestroy, Signal, signal } from '@angular/core';
import { LanguageService } from './language.service';
import { languages, languagesResponse } from '../models/languages.models';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { safeLocalStorageGetItem } from '../core/utils/safe-storage.util';

declare global {
  interface Window {
    __env?: { googleMapsApiKey: string };
  }
}

const MAPS_LOAD_TIMEOUT_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class InitialService implements OnDestroy {
  private _languages = signal<languages[]>([]);
  private mapsLoadPromise: Promise<boolean> | null = null;
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private _LanguageService: LanguageService) {}

  /** Lazy-load Google Maps; safe to call from map components. Never throws. */
  async ensureGoogleMapsLoaded(): Promise<boolean> {
    if (this.isGoogleMapsReady()) {
      return true;
    }

    if (!this.mapsLoadPromise) {
      this.mapsLoadPromise = this.loadGoogleMapsScript();
    }

    return this.mapsLoadPromise;
  }

  private isGoogleMapsReady(): boolean {
    const googleMaps = window['google'] as typeof google | undefined;
    return !!googleMaps?.maps;
  }

  private loadGoogleMapsScript(): Promise<boolean> {
    const language = safeLocalStorageGetItem('language') || 'en';

    return new Promise(resolve => {
      const timeoutId = window.setTimeout(() => {
        console.warn('[InitialService] Google Maps script load timed out');
        resolve(false);
      }, MAPS_LOAD_TIMEOUT_MS);

      const finish = (loaded: boolean) => {
        window.clearTimeout(timeoutId);
        resolve(loaded);
      };

      window.setTimeout(() => {
        if (
          this.isGoogleMapsReady() ||
          document.getElementById('testScriptName')
        ) {
          finish(true);
          return;
        }

        const head = document.getElementsByTagName('head')[0];
        if (!head) {
          console.warn(
            '[InitialService] Document head not available for Maps script'
          );
          finish(false);
          return;
        }

        const apiKey = environment.GOOGLE_API_KEY;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://maps.googleapis.com/maps/api/js?v=quarterly&key=${apiKey}&libraries=marker,visualization,places,geometry&language=${language}`;
        script.id = 'testScriptName';
        script.async = true;
        script.defer = true;

        script.onload = () => {
          finish(this.isGoogleMapsReady());
        };

        script.onerror = error => {
          console.warn(
            '[InitialService] Google Maps script failed to load:',
            error
          );
          finish(false);
        };

        head.appendChild(script);
      }, 100);
    });
  }

  async getLanguages(): Promise<void> {
    this._LanguageService
      .getLanguages('')
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: languagesResponse) => {
          this._languages.set(res.list);
        },
      });
  }

  get languages(): Signal<languages[]> {
    return this._languages;
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
